import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Users,
  UserPlus,
  Trash2,
  RefreshCw,
  Search,
  Shield,
  Crown,
  Moon,
  Sun,
  LogOut,
  ArrowLeft,
  Mail,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Gift
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminPanelNew({ user, theme, toggleTheme, onLogout, onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  
  // New user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserName, setNewUserName] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      
      // Calculate stats
      const total = res.data.users?.length || 0;
      const active = res.data.users?.filter(u => u.subscription_status === "active").length || 0;
      setStats({ total, active, expired: total - active });
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleGiveFreePlan = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      // Give 1 month free subscription
      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() + 1);
      
      await axios.post(`${API}/admin/users/${userId}/subscription`, {
        subscription_type: "monthly",
        subscription_status: "active",
        subscription_expires: expiryDate.toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("1 month free subscription granted!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to grant subscription");
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail || !newUserPassword || !newUserName) {
      toast.error("Fill all fields");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/create-user`, {
        email: newUserEmail,
        password: newUserPassword,
        name: newUserName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created!");
      setShowAddUser(false);
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserName("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isDark = theme === "dark";

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDark ? 'bg-[#0a0a0a]/95 border-[#1f1f1f]' : 'bg-white/95 border-gray-200'} border-b backdrop-blur`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-500/20 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#ccff00]" />
              <span className="font-bold text-lg">ADMIN PANEL</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 hover:bg-gray-500/20 rounded">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={onLogout} className="p-2 hover:bg-red-500/20 rounded text-red-500">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border`}>
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
              <div>
                <div className="text-xl md:text-2xl font-bold">{stats.total}</div>
                <div className="text-xs md:text-sm text-gray-500">Total Users</div>
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border`}>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
              <div>
                <div className="text-xl md:text-2xl font-bold">{stats.active}</div>
                <div className="text-xs md:text-sm text-gray-500">Active</div>
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border`}>
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
              <div>
                <div className="text-xl md:text-2xl font-bold">{stats.expired}</div>
                <div className="text-xs md:text-sm text-gray-500">Inactive</div>
              </div>
            </div>
          </div>
        </div>

        {/* FREE ACCESS BANNER */}
        <div className="mb-6 p-3 md:p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Gift className="w-5 h-5 md:w-6 md:h-6 text-green-500 flex-shrink-0" />
            <div>
              <div className="font-bold text-green-500 text-sm md:text-base">FREE ACCESS UNTIL SEPTEMBER 1, 2026</div>
              <div className="text-xs md:text-sm text-gray-400">All users have free access. No restrictions.</div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className={`p-3 md:p-4 rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border mb-6`}>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-[#27272a] border-[#3f3f46]' : ''}`}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchUsers} variant="outline" className="flex-1 sm:flex-none gap-2">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button onClick={() => setShowAddUser(!showAddUser)} className="flex-1 sm:flex-none gap-2 bg-[#ccff00] text-black hover:bg-[#b8e600]">
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Add User</span>
              </Button>
            </div>
          </div>

          {/* Add User Form */}
          {showAddUser && (
            <form onSubmit={handleAddUser} className={`mt-4 p-3 md:p-4 rounded-lg ${isDark ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="User name"
                    className={isDark ? 'bg-[#18181b] border-[#3f3f46]' : ''}
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    className={isDark ? 'bg-[#18181b] border-[#3f3f46]' : ''}
                  />
                </div>
                <div>
                  <Label className="text-xs">Password</Label>
                  <Input
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="Password"
                    className={isDark ? 'bg-[#18181b] border-[#3f3f46]' : ''}
                  />
                </div>
                <div className="flex items-end">
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    Create
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Users List - Mobile Friendly Cards */}
        <div className="space-y-3">
          {loading ? (
            <div className={`p-8 text-center rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-white'} border ${isDark ? 'border-[#27272a]' : 'border-gray-200'}`}>
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className={`p-8 text-center rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-white'} border ${isDark ? 'border-[#27272a]' : 'border-gray-200'}`}>
              No users found
            </div>
          ) : (
            filteredUsers.map((u) => (
              <div 
                key={u.id} 
                className={`p-3 md:p-4 rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${u.is_admin ? 'bg-[#ccff00] text-black' : isDark ? 'bg-[#27272a]' : 'bg-gray-200'}`}>
                      {u.is_admin ? <Crown className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span className="truncate">{u.name || "No name"}</span>
                        {u.is_admin && <span className="text-xs bg-[#ccff00] text-black px-1.5 py-0.5 rounded flex-shrink-0">ADMIN</span>}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500 flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{u.email}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                    u.subscription_status === "active" 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-red-500/20 text-red-500'
                  }`}>
                    {u.subscription_status === "active" ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    <span className="hidden sm:inline">{u.subscription_status || "inactive"}</span>
                  </span>
                </div>
                
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}
                    </span>
                    <span>{u.subscription_type || "Free"}</span>
                  </div>
                  
                  {!u.is_admin && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGiveFreePlan(u.id)}
                        className="h-7 px-2 text-xs text-green-500 border-green-500/50 hover:bg-green-500/20"
                      >
                        <Gift className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Free Month</span>
                        <span className="sm:hidden">+1M</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteUser(u.id, u.email)}
                        className="h-7 px-2 text-xs text-red-500 border-red-500/50 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
